import { ChangeDetectionStrategy, Component, inject, signal, computed, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { doc, getDoc, getDocs, collection, query, where, updateDoc, arrayUnion, arrayRemove, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { AuthService } from './auth.service';
import { ThemeService } from './theme';
import { NotificationService } from './notification';
import { UserImage } from './models/image.model';
import { AuthProfile } from './models/auth.model';
import { CountFormatPipe } from './count-format.pipe';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
  };
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-images',
  imports: [CommonModule, MatIconModule, RouterModule, CountFormatPipe],
  template: `<div class="min-h-screen w-full bg-transparent text-slate-900 dark:text-white transition-colors duration-500 relative overflow-x-hidden flex flex-col font-sans">
  <!-- Fixed Sticky Header with Blurred Background (Profile Page Aesthetic) -->
  <header class="ios-header-liquid fixed top-0 inset-x-0 h-[56px] md:h-[64px] border-b border-black/5 dark:border-white/10 bg-white/70 dark:bg-zinc-950/70 backdrop-blur-3xl px-4 md:px-6 flex items-center justify-between z-[100] select-none transition-all duration-300">
    <!-- Premium glass gradient underline -->
    <div class="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-black/5 dark:via-white/10 to-transparent"></div>

    <!-- Left Group: Navigation Controls & Profile Info mirroring profile header -->
    <div class="relative z-10 flex items-center gap-2 md:gap-3 flex-1 min-w-0 h-full">
      <!-- Interactive Back Button (Exact Mirror of Profile Back Button) -->
      <button 
        type="button"
        (click)="goBack()" 
        class="ios-back-btn shrink-0 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-all active:scale-90 group cursor-pointer" 
        title="Back"
        id="image-detail-back-btn"
      >
        <mat-icon class="!text-[20px] md:!text-[22px] text-ios-blue dark:text-sky-400 group-hover:-translate-x-0.5 transition-transform">
          arrow_back_ios_new
        </mat-icon>
      </button>

      <!-- Owner Profile Avatar & Names Sitting Completely Flush -->
      @if (ownerProfile(); as owner) {
        <div class="flex items-center min-w-0 flex-1 h-full pl-1">
          @if (owner.photoURL) {
            <img 
              [src]="owner.photoURL" 
              [alt]="owner.displayName" 
              class="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover flex-shrink-0 border border-black/10 dark:border-white/20 shadow-sm transition-transform active:scale-90" 
              referrerpolicy="no-referrer"
            />
          } @else {
            <div class="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-ios-blue text-white flex items-center justify-center text-xs sm:text-[13px] font-black flex-shrink-0 capitalize shadow-sm">
              {{ (owner.displayName || 'K').charAt(0).toUpperCase() }}
            </div>
          }

          <div class="ml-2.5 flex flex-col justify-center min-w-0 flex-1 select-text">
            <div class="flex items-center min-w-0 leading-none">
              <h1 class="text-base sm:text-lg font-bold text-gray-900 dark:text-white tracking-tight leading-none truncate">
                {{ owner.displayName }}
              </h1>
            </div>
            <span class="text-[11px] sm:text-[12px] font-normal text-gray-500 dark:text-zinc-500 leading-none lowercase truncate mt-1">
              @{{ owner.username || owner.uid.substring(0, 6) }}
            </span>
          </div>
        </div>
      }
    </div>
  </header>

  <!-- Main Showcase and Details Layout -->
  <main class="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-8 pt-20 sm:pt-24 pb-12 flex flex-col gap-8 md:gap-10">
    
    @if (isLoading()) {
      <div class="flex-1 flex flex-col items-center justify-center py-20 gap-4">
        <div class="w-12 h-12 border-4 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin"></div>
        <p class="text-sm text-gray-500 dark:text-gray-400 font-medium">Loading asset details...</p>
      </div>
    } @else if (!activeImage()) {
      <div class="flex-1 flex flex-col items-center justify-center py-20 text-center space-y-4">
        <div class="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 mx-auto">
          <mat-icon class="!text-3xl h-8 w-8">error_outline</mat-icon>
        </div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">Asset Not Found</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 max-w-md">
          The requested photo asset details could not be found or has been removed.
        </p>
        <button (click)="goBack()" class="px-5 py-2 rounded-xl bg-ios-blue hover:bg-ios-blue/90 text-white font-bold text-sm transition-all active:scale-95">
          Go Back
        </button>
      </div>
    } @else {
      @let img = activeImage()!;
      
      <!-- Responsive Two Column Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
        
        <!-- Left/Center: Showcase Image Container (7 cols) -->
        <div class="lg:col-span-7 flex flex-col gap-4">
          <div class="relative w-full overflow-hidden bg-black/60 rounded-[32px] sm:rounded-[40px] border border-black/5 dark:border-white/10 shadow-ios-md group flex items-center justify-center max-h-[70vh]">
            <!-- Ambient glow behind image -->
            <div class="absolute inset-x-12 inset-y-8 bg-cover bg-center blur-[100px] opacity-40 -z-10 pointer-events-none" [style.backgroundImage]="'url(' + img.imageUrl + ')'"></div>
            
            <img 
              [src]="img.imageUrl" 
              alt="Active Showcase" 
              class="w-full h-auto max-h-[66vh] object-contain rounded-[24px]"
              referrerpolicy="no-referrer"
            />
            
            <!-- Single Icon Like Button at Top Corner -->
            <button 
              type="button"
              (click)="toggleLike()" 
              class="absolute top-4 right-4 z-20 w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-xl transition-all active:scale-90 shadow-xl border cursor-pointer"
              [class.bg-red-500/25]="isLiked()"
              [class.border-red-500/50]="isLiked()"
              [class.text-red-500]="isLiked()"
              [class.bg-black/60]="!isLiked()"
              [class.border-white/20]="!isLiked()"
              [class.text-white]="!isLiked()"
              [class.hover:bg-black/80]="!isLiked()"
              title="Like photograph"
              aria-label="Like photograph"
            >
              <mat-icon class="!text-[22px] transition-transform duration-200" [class.scale-110]="isLiked()">
                {{ isLiked() ? 'favorite' : 'favorite_border' }}
              </mat-icon>
            </button>
          </div>
        </div>

        <!-- Right: Asset Details Sidebar (5 cols) -->
        <div class="lg:col-span-5 space-y-6">
          
          <!-- Dual-Box Grid Layout for Views and Likes (Matching Profile Page Metric Cards) -->
          <div class="grid grid-cols-2 gap-4 sm:gap-6">
            <!-- Views Metric Box -->
            <div 
              class="metric-card group w-full text-center border border-black/5 dark:border-white/5 bg-black/5 dark:bg-white/5 rounded-3xl p-5 sm:p-6 transition-all hover:scale-[1.02] relative flex flex-col items-center justify-center shadow-sm"
            >
              <mat-icon class="!text-[28px] sm:!text-[32px] text-ios-blue mb-1">visibility</mat-icon>
              <span class="metric-value text-ios-blue drop-shadow-sm group-hover:scale-105 transition-transform duration-300 relative z-10">
                {{ (img.viewedBy.length || 0) | countFormat }}
              </span>
              <span class="text-[11px] font-black uppercase tracking-[0.25em] text-slate-700 dark:text-zinc-300 opacity-80 mt-1 relative z-10">
                Views
              </span>
            </div>

            <!-- Likes Metric Box (Interactive) -->
            <button 
              type="button"
              (click)="toggleLike()" 
              class="metric-card group cursor-pointer w-full text-center border border-black/5 dark:border-white/5 bg-black/5 dark:bg-white/5 rounded-3xl p-5 sm:p-6 transition-all hover:scale-[1.02] active:scale-[0.98] relative flex flex-col items-center justify-center shadow-sm"
            >
              <mat-icon 
                class="!text-[28px] sm:!text-[32px] mb-1 transition-transform group-hover:scale-110"
                [class.text-ios-red]="isLiked()"
                [class.text-slate-400]="!isLiked()"
                [class.dark:text-zinc-500]="!isLiked()"
              >
                {{ isLiked() ? 'favorite' : 'favorite_border' }}
              </mat-icon>
              <span 
                class="metric-value drop-shadow-sm group-hover:scale-105 transition-transform duration-300 relative z-10"
                [class.text-ios-red]="isLiked()"
                [class.text-slate-900]="!isLiked()"
                [class.dark:text-zinc-100]="!isLiked()"
              >
                {{ (img.likedBy.length || 0) | countFormat }}
              </span>
              <span class="text-[11px] font-black uppercase tracking-[0.25em] text-slate-700 dark:text-zinc-300 opacity-80 mt-1 relative z-10">
                {{ isLiked() ? 'Liked' : 'Likes' }}
              </span>
            </button>
          </div>

          <!-- Asset Header Card (Rounded, Shadowed Container) -->
          <div class="bento-card p-6 sm:p-7 space-y-2">
            <div>
              <span class="text-[10px] uppercase tracking-[0.25em] font-black text-ios-blue">Kicgle Creative Asset</span>
              <h2 class="text-2xl font-black tracking-tight mt-1 text-gray-900 dark:text-white">
                Photograph
              </h2>
            </div>
            
            <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
              A visually high-contrast photo asset carefully curated in the Kicgle hub library.
            </p>
          </div>

          <!-- Ownership Card (Rounded, Shadowed Container) -->
          <div class="bento-card p-6 sm:p-7 space-y-4">
            <span class="text-[10px] font-black uppercase tracking-[0.35em] text-gray-500 dark:text-gray-400 opacity-80 px-1">
              Ownership & Attribution
            </span>
            
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- Uploaded By -->
              @if (uploaderProfile(); as uploader) {
                <div class="p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex items-center gap-3.5">
                  <img 
                    [src]="uploader.photoURL || 'https://picsum.photos/seed/uploader/100/100'" 
                    alt="Uploader Avatar" 
                    class="w-10 h-10 rounded-full border border-black/10 dark:border-white/10 object-cover shadow-sm"
                    referrerpolicy="no-referrer"
                  />
                  <div class="flex flex-col truncate">
                    <span class="text-[10px] uppercase tracking-wider text-gray-500 dark:text-zinc-400 font-bold leading-none mb-1">Uploaded By</span>
                    <span class="text-xs font-bold text-gray-900 dark:text-white truncate">{{ uploader.displayName }}</span>
                    <span class="text-[10px] font-mono opacity-60 truncate text-gray-500 dark:text-zinc-400">@{{ uploader.username || uploader.uid.substring(0,6) }}</span>
                  </div>
                </div>
              } @else {
                <div class="p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex items-center gap-3.5">
                  <div class="w-10 h-10 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center text-gray-500">
                    <mat-icon>account_circle</mat-icon>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-[11px] text-gray-500 dark:text-zinc-400 leading-none">Uploaded By</span>
                    <span class="text-xs font-bold text-gray-900 dark:text-white">Kicgle Artist</span>
                  </div>
                </div>
              }

              <!-- Owned By -->
              @if (ownerProfile(); as owner) {
                <div class="p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex items-center gap-3.5">
                  <img 
                    [src]="owner.photoURL || 'https://picsum.photos/seed/owner/100/100'" 
                    alt="Owner Avatar" 
                    class="w-10 h-10 rounded-full border border-black/10 dark:border-white/10 object-cover shadow-sm"
                    referrerpolicy="no-referrer"
                  />
                  <div class="flex flex-col truncate">
                    <span class="text-[10px] uppercase tracking-wider text-gray-500 dark:text-zinc-400 font-bold leading-none mb-1">Owned By</span>
                    <span class="text-xs font-bold text-gray-900 dark:text-white truncate">{{ owner.displayName }}</span>
                    <span class="text-[10px] font-mono opacity-60 truncate text-gray-500 dark:text-zinc-400">@{{ owner.username || owner.uid.substring(0,6) }}</span>
                  </div>
                </div>
              } @else {
                <div class="p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex items-center gap-3.5">
                  <div class="w-10 h-10 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center text-gray-500">
                    <mat-icon>verified</mat-icon>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-[11px] text-gray-500 dark:text-zinc-400 leading-none">Owned By</span>
                    <span class="text-xs font-bold text-gray-900 dark:text-white">Kicgle Member</span>
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- Image Details Card (Rounded, Shadowed Container) -->
          <div class="bento-card p-6 sm:p-7 space-y-4">
            <span class="text-[10px] font-black uppercase tracking-[0.35em] text-gray-500 dark:text-gray-400 opacity-80 px-1">
              Image Details
            </span>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- Visibility Status -->
              <div class="p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex flex-col justify-between">
                <span class="text-[10px] uppercase text-gray-500 dark:text-zinc-400 font-bold tracking-wider mb-2 flex items-center gap-1.5">
                  <mat-icon class="!text-[16px] text-ios-blue">visibility</mat-icon>
                  Visibility Status
                </span>
                <div>
                  <span 
                    class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-black tracking-wider uppercase rounded-full"
                    [class.bg-emerald-500/10]="(img.visibility || 'public') === 'public'"
                    [class.text-emerald-600]="(img.visibility || 'public') === 'public'"
                    [class.dark:text-emerald-400]="(img.visibility || 'public') === 'public'"
                    [class.bg-amber-500/10]="(img.visibility || 'public') === 'private'"
                    [class.text-amber-600]="(img.visibility || 'public') === 'private'"
                    [class.dark:text-amber-400]="(img.visibility || 'public') === 'private'"
                  >
                    <span class="w-2 h-2 rounded-full" [class.bg-emerald-500]="(img.visibility || 'public') === 'public'" [class.bg-amber-500]="(img.visibility || 'public') === 'private'"></span>
                    {{ img.visibility || 'public' }}
                  </span>
                </div>
              </div>

              <!-- Approval Status -->
              <div class="p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex flex-col justify-between">
                <span class="text-[10px] uppercase text-gray-500 dark:text-zinc-400 font-bold tracking-wider mb-2 flex items-center gap-1.5">
                  <mat-icon class="!text-[16px] text-orange-400">verified</mat-icon>
                  Approval Status
                </span>
                <div>
                  <span 
                    class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-black tracking-wider uppercase rounded-full"
                    [class.bg-emerald-500/10]="isApproved()"
                    [class.text-emerald-600]="isApproved()"
                    [class.dark:text-emerald-400]="isApproved()"
                    [class.bg-orange-500/10]="!isApproved() && img.verificationStatus !== 'rejected'"
                    [class.text-orange-600]="!isApproved() && img.verificationStatus !== 'rejected'"
                    [class.dark:text-orange-400]="!isApproved() && img.verificationStatus !== 'rejected'"
                    [class.bg-red-500/10]="img.verificationStatus === 'rejected'"
                    [class.text-red-600]="img.verificationStatus === 'rejected'"
                    [class.dark:text-red-400]="img.verificationStatus === 'rejected'"
                  >
                    <span class="w-2 h-2 rounded-full" [class.bg-emerald-500]="isApproved()" [class.bg-orange-400]="!isApproved() && img.verificationStatus !== 'rejected'" [class.bg-red-500]="img.verificationStatus === 'rejected'"></span>
                    {{ isApproved() ? 'approved' : (img.verificationStatus || 'review') }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Action Buttons: Reject and Approve (HIDDEN FOR APPROVED IMAGES) -->
            @if ((hasManagementPermission() || authService.isAdmin()) && !isApproved()) {
              <div class="pt-2 flex items-center gap-3">
                <button 
                  type="button"
                  (click)="rejectImage()"
                  [disabled]="isDeleting() || isSaving()"
                  class="flex-1 h-12 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-500 hover:bg-red-500 hover:text-white font-bold flex items-center justify-center gap-2 transition-all active:scale-95 text-sm disabled:opacity-50 cursor-pointer shadow-sm"
                >
                  @if (isDeleting()) {
                    <div class="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  } @else {
                    <mat-icon class="!text-[18px]">block</mat-icon>
                    <span>Reject</span>
                  }
                </button>
                <button 
                  type="button"
                  (click)="approveImage()"
                  [disabled]="isDeleting() || isSaving()"
                  class="flex-1 h-12 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 shadow-[0_10px_20px_rgba(16,185,129,0.25)] transition-all active:scale-95 text-sm disabled:opacity-50 cursor-pointer"
                >
                  @if (isSaving()) {
                    <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Approving...</span>
                  } @else {
                    <mat-icon class="!text-[18px]">check_circle</mat-icon>
                    <span>Approve</span>
                  }
                </button>
              </div>
            }
          </div>

        </div>
      </div>

      <!-- Related Photos Section (Rounded, Shadowed Container) -->
      @if (relatedImages().length > 0) {
        <div class="bento-card p-6 sm:p-8 space-y-6">
          <div class="flex items-center justify-between">
            <h3 class="text-lg sm:text-xl font-black tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
              <mat-icon class="text-ios-blue">photo_library</mat-icon>
              <span>More Photos by Creator</span>
            </h3>
            <span class="text-[10px] uppercase bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 px-3 py-1 rounded-full font-bold opacity-70 text-gray-700 dark:text-zinc-300">
              {{ relatedImages().length }} items
            </span>
          </div>

          <!-- Horizontal/Scroll Gallery Grid -->
          <div class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4">
            @for (relatedImg of relatedImages(); track relatedImg.id) {
              <div 
                (click)="selectRelatedImage(relatedImg)"
                (keydown.enter)="selectRelatedImage(relatedImg)"
                tabindex="0"
                class="group relative aspect-square overflow-hidden rounded-2xl border bg-black/5 dark:bg-zinc-900 cursor-pointer hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ios-blue"
                [class.border-ios-blue]="relatedImg.id === img.id"
                [class.border-black/5]="relatedImg.id !== img.id"
                [class.dark:border-white/5]="relatedImg.id !== img.id"
                [class.ring-2]="relatedImg.id === img.id"
                [class.ring-ios-blue]="relatedImg.id === img.id"
              >
                <img 
                  [src]="relatedImg.imageUrl" 
                  alt="Related Art" 
                  class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  referrerpolicy="no-referrer"
                />
                
                @if (relatedImg.id === img.id) {
                  <!-- Active image overlay badge -->
                  <div class="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <span class="text-[10px] uppercase tracking-wider bg-ios-blue text-white px-2 py-0.5 rounded-full font-black">Active</span>
                  </div>
                } @else {
                  <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-2">
                    <span class="text-[9px] uppercase tracking-wider text-white font-bold bg-black/70 px-1.5 py-0.5 rounded">View Info</span>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    }

    <!-- Sticky Mobile Action Footer for Management/Admin (Hidden for approved images) -->
    @if ((hasManagementPermission() || authService.isAdmin()) && activeImage() && !isApproved()) {
      <div class="fixed bottom-0 inset-x-0 z-40 p-3 bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 flex items-center justify-center gap-3 sm:hidden shadow-[0_-8px_20px_rgba(0,0,0,0.5)]">
        <button 
          type="button"
          (click)="rejectImage()"
          [disabled]="isDeleting() || isSaving()"
          class="flex-1 h-12 rounded-xl bg-red-500/15 border border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white font-bold flex items-center justify-center gap-2 transition-all active:scale-95 text-sm disabled:opacity-50"
        >
          @if (isDeleting()) {
            <div class="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin"></div>
            <span>Deleting...</span>
          } @else {
            <mat-icon class="!text-[18px]">block</mat-icon>
            <span>Reject</span>
          }
        </button>
        <button 
          type="button"
          (click)="approveImage()"
          [disabled]="isDeleting() || isSaving()"
          class="flex-1 h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 shadow-[0_10px_20px_rgba(16,185,129,0.25)] transition-all active:scale-95 text-sm disabled:opacity-50"
        >
          @if (isSaving()) {
            <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            <span>Approving...</span>
          } @else {
            <mat-icon class="!text-[18px]">check_circle</mat-icon>
            <span>Approve</span>
          }
        </button>
      </div>
    }

    <!-- Edit Modal -->
    @if (isEditModalOpen()) {
      <div class="fixed inset-0 z-[1000] flex items-center justify-center px-4">
        <!-- Backdrop button for accessibility and dismiss -->
        <button 
          type="button" 
          (click)="closeEditModal()" 
          class="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fade-in cursor-default block w-full h-full border-0" 
          aria-label="Close edit modal"
        ></button>
        
        <!-- Modal Content Container -->
        <div class="relative w-full max-w-md bg-zinc-950 border border-white/10 rounded-[28px] max-h-[90vh] overflow-y-auto p-6 md:p-8 space-y-6 shadow-2xl animate-ios-slide font-sans text-left">
          
          <div class="flex items-center justify-between border-b border-white/5 pb-4">
            <h3 class="text-lg font-black text-white flex items-center gap-2">
              <mat-icon class="text-ios-blue">edit</mat-icon>
              <span>Edit Photograph Settings</span>
            </h3>
            <button (click)="closeEditModal()" class="text-gray-400 hover:text-white transition-colors" aria-label="Close">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div class="space-y-4">
            <div>
              <p class="text-xs text-amber-400 font-medium bg-amber-500/10 rounded-lg p-2.5 mb-4 border border-amber-500/20">
                🔒 Image file replacement is restricted to protect archival integrity. Only license type and visibility state can be modified.
              </p>
            </div>

            <!-- License Dropdown -->
            <div class="flex flex-col gap-1.5">
              <label for="editLicenseSelect" class="text-[11px] font-bold uppercase tracking-widest text-gray-500">License Type</label>
              <select 
                id="editLicenseSelect"
                [value]="editLicenseType()" 
                (change)="editLicenseType.set($any($event.target).value)"
                class="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:border-ios-blue focus:outline-none"
              >
                <option value="standard">Standard License</option>
                <option value="commercial">Commercial Pro License</option>
                <option value="open-source">Open Source Creative Commons</option>
              </select>
            </div>

            <!-- Visibility / Status Toggle -->
            <div class="flex flex-col gap-1.5">
              <label for="editVisibilitySelect" class="text-[11px] font-bold uppercase tracking-widest text-gray-500">Post Status</label>
              <select 
                id="editVisibilitySelect"
                [value]="editStatus()" 
                (change)="editStatus.set($any($event.target).value)"
                class="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:border-ios-blue focus:outline-none"
              >
                <option value="published">Published</option>
                <option value="unpublished">Unpublished</option>
              </select>
            </div>

            <!-- Verification Status Toggle -->
            <div class="flex flex-col gap-1.5">
              <label for="editVerificationSelect" class="text-[11px] font-bold uppercase tracking-widest text-gray-500">Verification Status</label>
              <select 
                id="editVerificationSelect"
                [value]="editVerificationStatus()" 
                (change)="editVerificationStatus.set($any($event.target).value)"
                class="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:border-ios-blue focus:outline-none"
              >
                <option value="review">Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-4 border-t border-white/5">
            <button 
              type="button" 
              (click)="closeEditModal()"
              [disabled]="isSaving()" 
              class="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 font-bold text-xs text-white uppercase tracking-tight transition-all active:scale-95 disabled:opacity-50"
            >
              Cancel
            </button>
            <button 
              type="button" 
              (click)="saveSettings()"
              [disabled]="isSaving()" 
              class="flex-1 py-2.5 rounded-xl bg-ios-blue hover:bg-ios-blue/90 font-bold text-xs text-white uppercase tracking-tight transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              @if (isSaving()) {
                <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Saving...</span>
              } @else {
                <span>Save Changes</span>
              }
            </button>
          </div>

        </div>
      </div>
    }

    <!-- Delete Confirmation Modal -->
    @if (isDeleteModalOpen()) {
      <div class="fixed inset-0 z-[1000] flex items-center justify-center px-4">
        <!-- Backdrop button for accessibility and dismiss -->
        <button 
          type="button" 
          (click)="closeDeleteModal()" 
          class="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fade-in cursor-default block w-full h-full border-0" 
          aria-label="Close delete confirmation"
        ></button>

        <!-- Modal Content -->
        <div class="relative w-full max-w-sm bg-zinc-950 border border-white/10 rounded-[28px] p-6 shadow-2xl space-y-6 text-center animate-ios-slide font-sans">
          
          <div class="w-14 h-14 rounded-full bg-ios-red/10 flex items-center justify-center text-ios-red mx-auto">
            <mat-icon class="!text-[28px] h-7 w-7">warning</mat-icon>
          </div>

          <div class="space-y-2">
            <h3 class="text-lg font-black text-white">Delete Photograph?</h3>
            <p class="text-xs text-gray-400 leading-relaxed font-semibold">
              Are you sure you want to delete this photo?<br>
              This action is permanent and irreversible. Both the catalog reference and Storage binary will be permanently deleted.
            </p>
          </div>

          <!-- Actions -->
          <div class="flex gap-3">
            <button 
              type="button" 
              (click)="closeDeleteModal()"
              [disabled]="isDeleting()"
              class="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 font-bold text-xs text-white uppercase tracking-tight transition-all active:scale-95 disabled:opacity-50"
            >
              Cancel
            </button>
            <button 
              type="button" 
              (click)="confirmDelete()"
              [disabled]="isDeleting()"
              class="flex-1 py-2.5 rounded-xl bg-ios-red hover:bg-ios-red/90 font-bold text-xs text-white uppercase tracking-tight transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              @if (isDeleting()) {
                <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Deleting...</span>
              } @else {
                <span>Delete Asset</span>
              }
            </button>
          </div>

        </div>
      </div>
    }
  </main>
</div>
`,
  styles: [
    `
      @reference "../styles.css";

      .metric-card {
        @apply bg-black/5 dark:bg-white/5 rounded-3xl p-5 sm:p-6 flex flex-col items-center justify-center transition-all hover:scale-[1.02] active:scale-[0.98] border border-black/5 dark:border-white/5 relative overflow-hidden;
      }
      .metric-value {
        @apply text-xl sm:text-2xl font-black tracking-tighter leading-none relative z-10;
      }
      .bento-card {
        @apply bg-white/60 dark:bg-zinc-900/60 backdrop-blur-3xl border border-black/5 dark:border-white/10 rounded-[32px] shadow-ios-sm overflow-hidden transition-all relative;
      }
    `,
  ],
})
export class ImagesView implements OnInit {
  public route = inject(ActivatedRoute);
  public router = inject(Router);
  public authService = inject(AuthService);
  public themeService = inject(ThemeService);
  public notificationService = inject(NotificationService);

  // States
  isLoading = signal<boolean>(true);
  ownerId = signal<string>('');
  imageId = signal<string>('');
  
  activeImage = signal<UserImage | null>(null);
  ownerProfile = signal<AuthProfile | null>(null);
  uploaderProfile = signal<AuthProfile | null>(null);
  relatedImages = signal<UserImage[]>([]);

  // Management controls states
  hasManagementPermission = signal<boolean>(false);
  isDeleteModalOpen = signal<boolean>(false);
  isEditModalOpen = signal<boolean>(false);
  editLicenseType = signal<string>('standard');
  editStatus = signal<string>('published');
  editVerificationStatus = signal<string>('review');
  isSaving = signal<boolean>(false);
  isDeleting = signal<boolean>(false);

  isDetailsDropdownOpen = signal<boolean>(true);

  // Derived properties
  isLiked = computed(() => {
    const user = this.authService.user();
    if (!user) return false;
    const img = this.activeImage();
    return img?.likedBy?.includes(user.uid) || img?.likes?.includes(user.uid) || false;
  });

  isApproved = computed(() => {
    const img = this.activeImage();
    if (!img) return false;
    return img.status === 'approved' || img.verificationStatus === 'approved';
  });

  toggleDetailsDropdown() {
    this.isDetailsDropdownOpen.update((open) => !open);
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const ownerId = params.get('ownerId') || '';
      const imageId = params.get('imageId') || '';
      
      if (ownerId && imageId) {
        this.ownerId.set(ownerId);
        this.imageId.set(imageId);
        this.loadAssetDetails(ownerId, imageId);
      } else {
        this.isLoading.set(false);
      }
    });
  }

  private handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
    const user = this.authService.user();
    const errInfo: FirestoreErrorInfo = {
      error: error instanceof Error ? error.message : String(error),
      authInfo: {
        userId: user?.uid,
        email: user?.email,
        emailVerified: user?.emailVerified,
        isAnonymous: user?.isAnonymous,
      },
      operationType,
      path,
    };
    console.error('Firestore Error details: ', JSON.stringify(errInfo));
    throw new Error(JSON.stringify(errInfo));
  }

  async loadAssetDetails(ownerId: string, imageId: string) {
    this.isLoading.set(true);
    try {
      // 1. Fetch targeted image document from `/accounts/{ownerId}/images/{imageId}`
      const imageDocPath = `accounts/${ownerId}/images/${imageId}`;
      const imageDocRef = doc(db, imageDocPath);
      let imageSnap;
      try {
        imageSnap = await getDoc(imageDocRef);
      } catch (err) {
        this.handleFirestoreError(err, OperationType.GET, imageDocPath);
        return;
      }

      if (!imageSnap.exists()) {
        this.activeImage.set(null);
        this.isLoading.set(false);
        return;
      }

      const imgData = { id: imageSnap.id, ...imageSnap.data() } as any;
      this.activeImage.set(imgData);

      // 2. Fetch owner's account details from `/accounts/{ownerId}`
      const ownerDocPath = `accounts/${ownerId}`;
      const ownerDocRef = doc(db, ownerDocPath);
      try {
        const ownerSnap = await getDoc(ownerDocRef);
        if (ownerSnap.exists()) {
          this.ownerProfile.set({ uid: ownerSnap.id, ...ownerSnap.data() } as AuthProfile);
        } else {
          this.ownerProfile.set(null);
        }
      } catch (err) {
        console.warn('Could not fetch owner profile:', err);
      }

      // Check roles/management permissions
      await this.checkManagementRole(ownerId);

      // 3. Fetch uploader's account details from `/accounts/{authorUid}` if it exists and differs from owner
      const authorUid = imgData.authorUid;
      if (authorUid) {
        if (authorUid === ownerId) {
          this.uploaderProfile.set(this.ownerProfile());
        } else {
          try {
            const uploaderSnap = await getDoc(doc(db, `accounts/${authorUid}`));
            if (uploaderSnap.exists()) {
              this.uploaderProfile.set({ uid: uploaderSnap.id, ...uploaderSnap.data() } as AuthProfile);
            }
          } catch (err) {
            console.warn('Could not fetch uploader profile:', err);
          }
        }
      } else {
        this.uploaderProfile.set(null);
      }

      // 4. Fetch related public images from the same owner
      const imagesCollPath = `accounts/${ownerId}/images`;
      const q = query(
        collection(db, imagesCollPath),
        where('status', '==', 'published')
      );
      try {
        const relatedSnap = await getDocs(q);
        const list: UserImage[] = relatedSnap.docs
          .map((d: any) => ({
            id: d.id,
            ...d.data(),
          } as any))
          .filter((item: any) =>
            item.id !== imageId &&
            item.status === 'published' &&
            item.verificationStatus === 'approved' &&
            item.visibility !== 'private'
          );
        this.relatedImages.set(list);
      } catch (err) {
        console.warn('Could not load related images:', err);
      }

      // 5. Increment views synchronously
      this.incrementViewCount(ownerId, imageId, imgData);

    } catch (err) {
      console.error('Error loading asset details:', err);
      this.notificationService.error('Error loading image details.');
    } finally {
      this.isLoading.set(false);
    }
  }

  async incrementViewCount(ownerId: string, imageId: string, img: UserImage) {
    const user = this.authService.user();
    // Use user UID or a unique anonymous token to track uniqueness
    const viewerId = user?.uid || 'anonymous_' + Math.random().toString(36).substring(2, 9);
    
    const viewedList = img.viewedBy || img.views || [];
    if (!viewedList.includes(viewerId)) {
      const updatedViews = [...viewedList, viewerId];
      // Update local state
      img.viewedBy = updatedViews;
      this.activeImage.set({ ...img });

      const imageDocPath = `accounts/${ownerId}/images/${imageId}`;
      try {
        await updateDoc(doc(db, imageDocPath), {
          viewedBy: arrayUnion(viewerId),
        });
      } catch (err) {
        console.warn('Could not increment views on server:', err);
      }
    }
  }

  async toggleLike() {
    const user = this.authService.user();
    if (!user) {
      this.notificationService.error('Please log in to like images.');
      return;
    }

    const img = this.activeImage();
    const ownerId = this.ownerId();
    const imageId = this.imageId();
    if (!img) return;

    const currentLikes = img.likedBy || img.likes || [];
    const isCurrentlyLiked = currentLikes.includes(user.uid);
    const updatedLikes = isCurrentlyLiked
      ? currentLikes.filter((id) => id !== user.uid)
      : [...currentLikes, user.uid];

    // Optimally update state
    const updatedImg = { ...img, likedBy: updatedLikes };
    this.activeImage.set(updatedImg);

    const imageDocPath = `accounts/${ownerId}/images/${imageId}`;
    try {
      await updateDoc(doc(db, imageDocPath), {
        likedBy: isCurrentlyLiked ? arrayRemove(user.uid) : arrayUnion(user.uid),
      });
    } catch (err) {
      console.error('Failed to update like in Firestore:', err);
      // Rollback
      img.likedBy = currentLikes;
      this.activeImage.set({ ...img });
      this.notificationService.error('Failed to save like change.');
    }
  }

  selectRelatedImage(relatedImg: UserImage) {
    if (relatedImg.id) {
      this.router.navigate(['/image', this.ownerId(), relatedImg.id]);
    }
  }

  getLicenseTitle(type: string | undefined): string {
    if (!type) return 'Standard';
    if (type === 'standard') return 'Standard';
    if (type === 'commercial') return 'Commercial Pro';
    if (type === 'open-source') return 'Open Source CC';
    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  formatDate(ts: unknown): string {
    if (!ts) return 'Recent';
    let date: Date | null = null;
    if (ts instanceof Date) {
      date = ts;
    } else {
      const timestamp = ts as { toDate?: () => Date; seconds?: number };
      if (typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof ts === 'string' || typeof ts === 'number') {
        date = new Date(ts);
      }
    }
    if (!date) return 'Recent';
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  private location = inject(Location);

  goBack() {
    const hasPreviousPage =
      typeof window !== 'undefined' &&
      window.history.length > 1 &&
      (window.history.state?.navigationId === undefined || window.history.state.navigationId > 1);
    if (hasPreviousPage) {
      this.location.back();
    } else {
      this.router.navigate(['/search']);
    }
  }

  async checkManagementRole(ownerId: string) {
    const currentUser = this.authService.user();
    if (!currentUser) {
      this.hasManagementPermission.set(false);
      return;
    }
    
    const userUid = currentUser.uid;
    // Founder / Owner fallback (if current user is the owner themselves)
    if (userUid === ownerId) {
      this.hasManagementPermission.set(true);
      return;
    }

    const profile = this.ownerProfile();
    if (profile && profile.accountType === 'organization') {
      try {
        const orgProfile = profile as { roles?: Record<string, string>; authorizedPersons?: { uid?: string; role?: string }[] };
        const rolesMap = orgProfile.roles;
        let userRole: string | undefined = undefined;

        if (rolesMap && typeof rolesMap === 'object' && !Array.isArray(rolesMap)) {
          userRole = rolesMap[userUid];
        } else if (Array.isArray(orgProfile.authorizedPersons)) {
          const found = orgProfile.authorizedPersons.find(
            (p) => p.uid === userUid
          );
          if (found) {
            userRole = found.role?.toLowerCase();
          }
        }

        if (userRole) {
          const roleLower = userRole.toLowerCase();
          const authorizedRoles = [
            'founder',
            'admin',
            'owner',
            'managing director',
            'managing trustee',
            'managing partner'
          ];
          if (authorizedRoles.includes(roleLower)) {
            this.hasManagementPermission.set(true);
            return;
          }
        }
      } catch (err) {
        console.error('Error checking organization roles in image view:', err);
      }
    }
    
    this.hasManagementPermission.set(false);
  }

  openEditModal() {
    const img = this.activeImage();
    if (img) {
      this.editLicenseType.set((img as any).license || 'standard');
      this.editStatus.set((img as any).status || 'published');
      this.editVerificationStatus.set(img.verificationStatus || 'review');
      this.isEditModalOpen.set(true);
    }
  }

  closeEditModal() {
    this.isEditModalOpen.set(false);
  }

  async saveSettings() {
    const img = this.activeImage();
    const ownerId = this.ownerId();
    const imageId = this.imageId();
    if (!img) return;

    this.isSaving.set(true);
    const updatedDocPath = `accounts/${ownerId}/images/${imageId}`;
    try {
      const prevStatus = img.verificationStatus || 'review';
      const newStatus = this.editVerificationStatus();
      let verifiedByValue = img.verifiedBy || '';

      if (prevStatus === 'review' && (newStatus === 'approved' || newStatus === 'rejected')) {
        verifiedByValue = this.authService.user()?.uid || '';
      }

      await updateDoc(doc(db, updatedDocPath), {
        license: this.editLicenseType() as any,
        status: this.editStatus(),
        verificationStatus: newStatus,
        verifiedBy: verifiedByValue
      });

      // Update active image state locally
      this.activeImage.set({
        ...img,
        verificationStatus: newStatus as any,
        verifiedBy: verifiedByValue
      });

      this.notificationService.success('Photo settings updated successfully.');
      this.isEditModalOpen.set(false);
    } catch (err) {
      console.error('Error saving image settings:', err);
      this.notificationService.error('Failed to update photo settings.');
    } finally {
      this.isSaving.set(false);
    }
  }

  openDeleteModal() {
    this.isDeleteModalOpen.set(true);
  }

  closeDeleteModal() {
    this.isDeleteModalOpen.set(false);
  }

  async confirmDelete() {
    const img = this.activeImage();
    const ownerId = this.ownerId();
    const imageId = this.imageId();
    if (!img) return;

    this.isDeleting.set(true);
    try {
      // 1. Delete physical storage asset if applicable
      const imageUrl = img.imageUrl;
      if (imageUrl && imageUrl.includes('firebasestorage.googleapis.com')) {
        try {
          const storageRef = ref(storage, imageUrl);
          await deleteObject(storageRef);
        } catch (storageErr) {
          console.warn('Physical storage asset deletion skipped or failed:', storageErr);
        }
      }

      // 2. Delete firestore document (source file)
      const imageDocPath = `accounts/${ownerId}/images/${imageId}`;
      await deleteDoc(doc(db, imageDocPath));

      this.notificationService.success('Photo asset deleted successfully.');
      this.isDeleteModalOpen.set(false);
      
      // Redirect to search keeping images c active
      this.router.navigate(['/search'], { queryParams: { c: 'images' } });
    } catch (err) {
      console.error('Error deleting image:', err);
      this.notificationService.error('Failed to delete photo asset.');
    } finally {
      this.isDeleting.set(false);
    }
  }

  async approveImage() {
    const img = this.activeImage();
    const ownerId = this.ownerId();
    const imageId = this.imageId();
    if (!img) return;

    this.isSaving.set(true);
    const updatedDocPath = `accounts/${ownerId}/images/${imageId}`;
    try {
      const updates = {
        verificationStatus: 'approved' as const,
        status: 'published' as const,
        verifiedBy: this.authService.user()?.uid || 'admin',
        verifiedAt: serverTimestamp()
      };

      await updateDoc(doc(db, updatedDocPath), updates);

      this.activeImage.update(current => current ? ({
        ...current,
        verificationStatus: 'approved',
        status: 'published',
        verifiedBy: this.authService.user()?.uid || 'admin'
      }) : null);

      this.notificationService.success('Photograph approved and published successfully.');
    } catch (err) {
      console.error('Error approving photo:', err);
      this.notificationService.error('Failed to approve photograph.');
    } finally {
      this.isSaving.set(false);
    }
  }

  async rejectImage() {
    await this.confirmDelete();
  }
}
